// RGM Studio Pro — real vocal FX built from native Web Audio nodes, so the
// same chain runs in live playback and in the OfflineAudioContext Final Mix.
// Non-destructive: processes the track bus only; recordings are never touched.

export const FX_PRESETS = [
  { id: "rap_presence", label: "Rap Presence", blurb: "Tight EQ, punchy compression, presence lift" },
  { id: "melodic", label: "Melodic", blurb: "Smooth EQ, gentle compression, lush reverb" },
  { id: "rnb", label: "R&B Vocal", blurb: "Warm EQ, smooth compression, reverb (+ delay)" },
  { id: "adlib_wide", label: "Ad-lib / Wide", blurb: "Bright, stereo-wide with a short stereo delay" },
];

export const DEFAULT_FX = {
  preset: "none", intensity: 60, bypass: false,
  eq: { on: false, low: 0, mid: 0, high: 0 },
  comp: { on: false, amount: 50 },
  reverb: { on: false, mix: 25, decay: 1.8 },
  delay: { on: false, time_ms: 320, feedback: 30, mix: 20 },
  width: { on: false, amount: 50 },
  gate: { on: false, amount: 40 },
};

// Noise gate (free for every artist): an AudioWorklet that fades the lane
// down to -40 dB when the voice drops under the threshold (room hiss,
// headphone bleed between lines). Same code runs live and in Final Mix.
const GATE_SRC = `
class RgmGate extends AudioWorkletProcessor {
  static get parameterDescriptors() { return [{ name: "threshold", defaultValue: -50 }, { name: "floor", defaultValue: -40 }]; }
  constructor() { super(); this.env = 0; this.g = 1; this.hold = 0; }
  process(inputs, outputs, params) {
    const inp = inputs[0], out = outputs[0];
    if (!inp || !inp.length) return true;
    const thr = Math.pow(10, params.threshold[0] / 20), floor = Math.pow(10, params.floor[0] / 20);
    const envRel = Math.exp(-1 / (sampleRate * 0.03)), atk = Math.exp(-1 / (sampleRate * 0.002)), rel = Math.exp(-1 / (sampleRate * 0.15));
    const holdN = Math.round(sampleRate * 0.06), n = inp[0].length;
    for (let i = 0; i < n; i++) {
      let x = 0;
      for (let c = 0; c < inp.length; c++) { const v = Math.abs(inp[c][i]); if (v > x) x = v; }
      this.env = x > this.env ? x : this.env * envRel;
      if (this.env > thr) this.hold = holdN; else if (this.hold > 0) this.hold--;
      const target = this.hold > 0 ? 1 : floor;
      this.g = target > this.g ? atk * this.g + (1 - atk) * target : rel * this.g + (1 - rel) * target;
      for (let c = 0; c < out.length; c++) out[c][i] = (inp[c] || inp[0])[i] * this.g;
    }
    return true;
  }
}
registerProcessor("rgm-gate", RgmGate);
`;

export function ensureGate(ctx) {
  if (!ctx.__rgmGate) {
    ctx.__rgmGate = (async () => {
      if (!ctx.audioWorklet || typeof AudioWorkletNode === "undefined") return false;
      const url = URL.createObjectURL(new Blob([GATE_SRC], { type: "application/javascript" }));
      try { await ctx.audioWorklet.addModule(url); ctx.__rgmGateReady = true; return true; }
      catch { return false; }
      finally { URL.revokeObjectURL(url); }
    })();
  }
  return ctx.__rgmGate;
}

export const gateActive = (fx) => !!(fx && !fx.bypass && fx.gate?.on);
// The free subset of a lane's FX — only the noise gate.
export const gateOnly = (fx) => (gateActive(fx) ? { ...DEFAULT_FX, gate: { ...DEFAULT_FX.gate, ...fx.gate } } : null);
const gateThresholdDb = (amount) => -70 + (Math.max(0, Math.min(100, amount ?? 40)) * 0.45);

export function normalizeFx(fx) {
  const f = fx || {};
  return {
    ...DEFAULT_FX, ...f,
    eq: { ...DEFAULT_FX.eq, ...(f.eq || {}) }, comp: { ...DEFAULT_FX.comp, ...(f.comp || {}) },
    reverb: { ...DEFAULT_FX.reverb, ...(f.reverb || {}) }, delay: { ...DEFAULT_FX.delay, ...(f.delay || {}) },
    width: { ...DEFAULT_FX.width, ...(f.width || {}) },
    gate: { ...DEFAULT_FX.gate, ...(f.gate || {}) },
  };
}

export function fxActive(fx) {
  if (!fx || fx.bypass) return false;
  return (fx.preset && fx.preset !== "none") || ["eq", "comp", "reverb", "delay", "width", "gate"].some((k) => fx[k]?.on);
}

// Pro-only modules (the gate is free).
export function proFxActive(fx) {
  if (!fx || fx.bypass) return false;
  return (fx.preset && fx.preset !== "none") || ["eq", "comp", "reverb", "delay", "width"].some((k) => fx[k]?.on);
}

// Preset + manual modules → concrete DSP parameters. Manual modules that
// are switched on override the preset's value for that stage.
export function resolveFx(raw) {
  const fx = normalizeFx(raw);
  const k = Math.max(0, Math.min(1, (fx.intensity ?? 60) / 100));
  const p = {
    hpf: 70, low: 0, lowF: 200, mid: 0, midF: 1500, high: 0, highF: 8000, presence: 0,
    comp: 0, reverbMix: 0, decay: 1.8, delayMix: 0, delayMs: 320, feedback: 0.3, width: 0,
  };
  if (fx.preset === "rap_presence") Object.assign(p, { hpf: 100, low: -2 * k, mid: -1 * k, midF: 350, presence: 6 * k, high: 2.5 * k, comp: 0.45 + 0.4 * k });
  if (fx.preset === "melodic") Object.assign(p, { hpf: 90, low: -1.5 * k, mid: -2 * k, midF: 450, presence: 2 * k, high: 3.5 * k, comp: 0.3 + 0.3 * k, reverbMix: 0.12 + 0.22 * k, decay: 2.4 });
  if (fx.preset === "rnb") Object.assign(p, { hpf: 80, low: 2.5 * k, lowF: 220, mid: -1.5 * k, midF: 500, presence: 1.5 * k, high: 2 * k, comp: 0.3 + 0.25 * k, reverbMix: 0.1 + 0.18 * k, decay: 2.8 });
  if (fx.preset === "adlib_wide") Object.assign(p, { hpf: 180, low: -3 * k, presence: 3 * k, high: 3 * k, comp: 0.35 + 0.3 * k, width: 0.5 + 0.5 * k, delayMix: 0.08 + 0.14 * k, delayMs: 180, feedback: 0.2 });
  if (fx.eq.on) Object.assign(p, { low: fx.eq.low, mid: fx.eq.mid, high: fx.eq.high, midF: 1500 });
  if (fx.comp.on) p.comp = fx.comp.amount / 100;
  if (fx.reverb.on) Object.assign(p, { reverbMix: fx.reverb.mix / 100, decay: fx.reverb.decay });
  if (fx.delay.on) Object.assign(p, { delayMix: fx.delay.mix / 100, delayMs: fx.delay.time_ms, feedback: fx.delay.feedback / 100 });
  if (fx.width.on) p.width = fx.width.amount / 100;
  return p;
}

// Longest audible tail the chain can add (for Final Mix length).
export function fxTailMs(raw) {
  if (!fxActive(raw)) return 0;
  const p = resolveFx(raw);
  const rev = p.reverbMix > 0 ? p.decay * 1000 : 0;
  const del = p.delayMix > 0 ? p.delayMs * Math.min(12, 1 + Math.log(0.001) / Math.log(Math.max(0.05, p.feedback))) : 0;
  return Math.min(8000, Math.round(Math.max(rev, del)));
}

function impulse(ctx, decay) {
  const len = Math.max(1, Math.floor(ctx.sampleRate * Math.min(6, decay)));
  const buf = ctx.createBuffer(2, len, ctx.sampleRate);
  let seed = 12345;
  const rnd = () => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 2147483648 - 1; };
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    for (let i = 0; i < len; i++) d[i] = rnd() * Math.pow(1 - i / len, 3.2);
  }
  return buf;
}

// Builds input → EQ → comp → (dry | reverb | delay) → width → safety → output.
export function buildFxChain(ctx, raw) {
  const input = ctx.createGain();
  const output = ctx.createGain();
  const nodes = [input, output];
  const mk = (n) => { nodes.push(n); return n; };
  if (!fxActive(raw)) {
    input.connect(output);
    return { input, output, dispose: () => nodes.forEach((n) => { try { n.disconnect(); } catch { /* */ } }) };
  }
  const p = resolveFx(raw);
  const biq = (type, f, g, q) => { const b = mk(ctx.createBiquadFilter()); b.type = type; b.frequency.value = f; if (g != null) b.gain.value = g; if (q != null) b.Q.value = q; return b; };
  const hpf = biq("highpass", p.hpf, null, 0.7);
  const fxn = normalizeFx(raw);
  if (fxn.gate.on && ctx.__rgmGateReady) {
    const gate = mk(new AudioWorkletNode(ctx, "rgm-gate", { parameterData: { threshold: gateThresholdDb(fxn.gate.amount), floor: -40 } }));
    input.connect(gate).connect(hpf);
  } else {
    input.connect(hpf);
  }
  const low = biq("lowshelf", p.lowF, p.low);
  const mid = biq("peaking", p.midF, p.mid, 0.9);
  const pres = biq("peaking", 4200, p.presence, 1.1);
  const high = biq("highshelf", p.highF, p.high);
  const comp = mk(ctx.createDynamicsCompressor());
  comp.threshold.value = -6 - 24 * p.comp;
  comp.ratio.value = 1 + 5 * p.comp;
  comp.knee.value = 10;
  comp.attack.value = 0.006;
  comp.release.value = 0.14;
  hpf.connect(low).connect(mid).connect(pres).connect(high).connect(comp);

  const bus = mk(ctx.createGain());
  const dry = mk(ctx.createGain());
  dry.gain.value = 1 - Math.min(0.5, p.reverbMix * 0.5 + p.delayMix * 0.3);
  comp.connect(dry).connect(bus);
  if (p.reverbMix > 0) {
    const conv = mk(ctx.createConvolver());
    conv.buffer = impulse(ctx, p.decay);
    const wet = mk(ctx.createGain());
    wet.gain.value = p.reverbMix * 0.9;
    comp.connect(conv).connect(wet).connect(bus);
  }
  if (p.delayMix > 0) {
    const dl = mk(ctx.createDelay(2));
    dl.delayTime.value = p.delayMs / 1000;
    const fb = mk(ctx.createGain());
    fb.gain.value = Math.min(0.85, p.feedback);
    const tone = biq("lowpass", 5000, null, 0.7);
    const wet = mk(ctx.createGain());
    wet.gain.value = p.delayMix;
    comp.connect(dl).connect(tone).connect(fb).connect(dl);
    tone.connect(wet).connect(bus);
  }

  let tail = bus;
  if (p.width > 0 && ctx.createStereoPanner && ctx.createChannelMerger) {
    // Mono-safe widening: centre stays dry; a short (11 ms) copy is placed
    // hard right and the direct copy hard left at reduced level, so a mono
    // sum only gets a shallow comb instead of a hollow phase cancel.
    const w = p.width * 0.45;
    const centre = mk(ctx.createGain()); centre.gain.value = 1 - w;
    const left = mk(ctx.createGain()); left.gain.value = w;
    const rightD = mk(ctx.createDelay(0.05)); rightD.delayTime.value = 0.011;
    const right = mk(ctx.createGain()); right.gain.value = w;
    const pl = mk(ctx.createStereoPanner()); pl.pan.value = -1;
    const pr = mk(ctx.createStereoPanner()); pr.pan.value = 1;
    const sum = mk(ctx.createGain());
    bus.connect(centre).connect(sum);
    bus.connect(left).connect(pl).connect(sum);
    bus.connect(rightD).connect(right).connect(pr).connect(sum);
    tail = sum;
  }
  // Safety limiter + trim: avoids clipping and sudden jumps when FX switch on.
  const trim = mk(ctx.createGain());
  trim.gain.value = Math.pow(10, -1.5 / 20);
  const safe = mk(ctx.createDynamicsCompressor());
  safe.threshold.value = -2; safe.ratio.value = 20; safe.knee.value = 0; safe.attack.value = 0.002; safe.release.value = 0.08;
  tail.connect(trim).connect(safe).connect(output);
  return { input, output, params: p, dispose: () => nodes.forEach((n) => { try { n.disconnect(); } catch { /* */ } }) };
}
